import { useState } from "react";
import { Loader2, CopyPlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { DAYS } from "./linearShared";

export const CopyDayDialog = ({ slots, onClose, onDone }) => {
  const firstWithSlots = DAYS.findIndex((_, i) => slots.some((s) => s.day === i));
  const [from, setFrom] = useState(Math.max(0, firstWithSlots));
  const [to, setTo] = useState([]);
  const [busy, setBusy] = useState(false);
  const others = DAYS.map((_, i) => i).filter((i) => i !== from);
  const count = slots.filter((s) => s.day === from).length;

  const toggle = (i) => setTo((t) => (t.includes(i) ? t.filter((x) => x !== i) : [...t, i]));
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/linear/copy-day", { from_day: from, to_days: to });
      toast.success(`Copied ${r.data.copied} slot${r.data.copied === 1 ? "" : "s"}`, { description: r.data.skipped ? `${r.data.skipped} skipped because those times were already taken` : undefined });
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't copy"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="copy-day-dialog" className="bg-[#0E131F] border-gray-800 text-white sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><CopyPlus className="h-5 w-5 text-amber-400" /> Copy a day</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">Existing slots on the other days are kept; any copied slot that would overlap them is skipped.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <p className="text-xs text-gray-400">Copy from</p>
          <select data-testid="copy-from" value={from} onChange={(e) => { setFrom(Number(e.target.value)); setTo([]); }} className="w-full h-9 bg-black/30 border border-gray-800 rounded-md px-2 text-sm text-white">
            {DAYS.map((d, i) => <option key={d} value={i}>{d} ({slots.filter((s) => s.day === i).length} slots)</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between"><p className="text-xs text-gray-400">Copy to</p>
            <button data-testid="copy-whole-week" onClick={() => setTo(to.length === others.length ? [] : others)} className="text-xs text-amber-300 hover:text-amber-200">{to.length === others.length ? "Clear" : "Whole week"}</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {others.map((i) => (
              <label key={i} className={"flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors " + (to.includes(i) ? "border-amber-500/50 bg-amber-500/10" : "border-gray-800 hover:border-gray-700")}>
                <Checkbox data-testid={`copy-to-${i}`} checked={to.includes(i)} onCheckedChange={() => toggle(i)} />{DAYS[i]}
              </label>
            ))}
          </div>
        </div>
        <div className="mt-2 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          <button data-testid="copy-submit" onClick={submit} disabled={busy || !to.length || !count} className="inline-flex items-center gap-2 rounded-lg gold-gradient px-4 py-2 text-sm font-semibold text-black hover:brightness-110 disabled:opacity-50">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Copy {count} slot{count === 1 ? "" : "s"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
